import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchSubscriptionPlans, formatPrice } from './pricing';

/**
 * Centralized Pricing Context
 * 
 * All subscription pricing data should be fetched from this context.
 * NO HARDCODED PRICES should exist anywhere in the codebase.
 * This context fetches plans from the API and provides them to all components.
 */

const PricingContext = createContext(null);

export const usePricing = () => {
    const context = useContext(PricingContext);
    if (!context) {
        throw new Error('usePricing must be used within a PricingProvider');
    }
    return context;
};

export const PricingProvider = ({ children }) => {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadPlans = async () => {
            try {
                setLoading(true);
                const data = await fetchSubscriptionPlans();
                setPlans(data.plans || []);
                setError(null);
            } catch (err) {
                console.error('Failed to load subscription plans:', err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        loadPlans();
    }, []);

    // Helper to get a specific plan by tier
    const getPlan = (tier) => {
        return plans.find(p => p.tier === tier) || null;
    };

    // Helper to get starter plan
    const getStarterPlan = () => getPlan('starter');

    // Helper to get professional plan
    const getProfessionalPlan = () => getPlan('professional');

    // Helper to get ultimate plan
    const getUltimatePlan = () => getPlan('ultimate');

    // Format price with currency
    const formatPlanPrice = (plan) => {
        if (!plan) return '...';
        return formatPrice(plan.price, plan.currency);
    };

    // Get per-month price for longer duration plans
    const getPerMonthPrice = (plan) => {
        if (!plan || !plan.duration_months || plan.duration_months <= 1) return null;
        return Math.round((plan.price || 0) / plan.duration_months);
    };

    const value = {
        plans,
        loading,
        error,
        getPlan,
        getStarterPlan,
        getProfessionalPlan,
        getUltimatePlan,
        formatPlanPrice,
        getPerMonthPrice,
        formatPrice,
    };

    return (
        <PricingContext.Provider value={value}>
            {children}
        </PricingContext.Provider>
    );
};

export default PricingContext;
